import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import scheduleService from '../services/scheduleService';
import { Play, Pause, Edit, Trash2, Copy, Eye, EyeOff, Plus, GripVertical } from 'lucide-react';
import Dialog from '../components/Dialog';
import ScheduleModal from '../components/ScheduleModal';
import { useDialog } from '../hooks/useDialog';
import { config } from '../config/env';
import './ScheduleDetail.css';

function ScheduleDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const { dialogState, closeDialog, confirm, alert } = useDialog();

    const [schedule, setSchedule] = useState(null);
    const [executions, setExecutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [executionsLoading, setExecutionsLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    useEffect(() => {
        if (currentWorkspace) {
            loadSchedule();
            loadExecutions();
        }
    }, [currentWorkspace, id]);

    const loadSchedule = async () => {
        try {
            setLoading(true);
            const response = await scheduleService.getSchedule(id, currentWorkspace.id);
            setSchedule(response.schedule);
        } catch (err) {
            console.error('Failed to load schedule:', err);
            await alert({
                title: 'Error',
                message: 'Failed to load schedule',
                variant: 'danger'
            });
            navigate('/schedules');
        } finally {
            setLoading(false);
        }
    };

    const loadExecutions = async () => {
        try {
            setExecutionsLoading(true);
            const response = await scheduleService.getScheduleExecutions(id, { limit: 10 });
            setExecutions(response.executions || []);
        } catch (err) {
            console.error('Failed to load executions:', err);
        } finally {
            setExecutionsLoading(false);
        }
    };

    const handlePauseResume = async () => {
        try {
            if (schedule.isPaused) {
                await scheduleService.resumeSchedule(id, currentWorkspace.id);
            } else {
                await scheduleService.pauseSchedule(id, currentWorkspace.id);
            }
            loadSchedule();
        } catch (err) {
            console.error('Failed to pause/resume:', err);
        }
    };

    const handleExecuteNow = async () => {
        const confirmed = await confirm({
            title: 'Execute Schedule Now',
            message: 'Execute this schedule immediately?',
            confirmText: 'Execute',
            variant: 'primary'
        });

        if (!confirmed) return;

        try {
            await scheduleService.executeScheduleNow(id, currentWorkspace.id);
            await alert({
                title: 'Success',
                message: 'Schedule execution started',
                variant: 'success'
            });
            setTimeout(loadExecutions, 2000);
        } catch (err) {
            console.error('Failed to execute:', err);
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirm({
            title: 'Delete Schedule',
            message: `Delete "${schedule.name}"? This cannot be undone.`,
            confirmText: 'Delete',
            variant: 'danger'
        });

        if (!confirmed) return;

        try {
            await scheduleService.deleteSchedule(id, currentWorkspace.id);
            navigate('/schedules');
        } catch (err) {
            console.error('Failed to delete:', err);
        }
    };



    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString();
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        }
        return `${seconds}s`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'failed': return 'error';
            case 'running': return 'warning';
            default: return 'default';
        }
    };

    if (loading) {
        return (
            <div className="schedule-detail">
                <div className="schedule-detail__loading">
                    <div className="spinner"></div>
                    <p>Loading schedule...</p>
                </div>
            </div>
        );
    }

    if (!schedule) {
        return null;
    }

    return (
        <div className="schedule-detail">
            {/* Header */}
            <div className="schedule-detail__header">
                <div className="schedule-detail__header-content">
                    <button className="schedule-detail__back" onClick={() => navigate('/schedules')}>
                        ← Back to Schedules
                    </button>
                    <h1>{schedule.name}</h1>
                    {schedule.description && <p>{schedule.description}</p>}
                    <div className="schedule-detail__meta">
                        <span className={`schedule-detail__status schedule-detail__status--${schedule.isPaused ? 'paused' : 'active'}`}>
                            {schedule.isPaused ? 'Paused' : 'Active'}
                        </span>
                        <span>Next run: {formatDate(schedule.nextRunAt)}</span>
                        {schedule.executionPlan && (
                            <Link
                                to={`/execution-plans/${schedule.executionPlanId}`}
                                className="schedule-detail__plan-link"
                            >
                                Plan: {schedule.executionPlan.name}
                            </Link>
                        )}
                    </div>
                </div>
                <div className="schedule-detail__actions">
                    <button className="btn btn--secondary" onClick={handlePauseResume}>
                        {schedule.isPaused ? <><Play size={16} /> Resume</> : <><Pause size={16} /> Pause</>}
                    </button>
                    <button className="btn btn--secondary" onClick={() => setShowEditModal(true)}>
                        <Edit size={16} /> Edit
                    </button>
                    {config.IS_DEV && (
                        <button className="btn btn--primary" onClick={handleExecuteNow}>
                            <Play size={16} /> Execute Now
                        </button>
                    )}
                    <button className="btn btn--secondary" onClick={handleDelete}>
                        <Trash2 size={16} /> Delete
                    </button>
                </div>
            </div>

            {/* Execution History */}
            <div className="schedule-detail__section">
                <div className="schedule-detail__section-header">
                    <h2>Execution History</h2>
                </div>

                {executionsLoading ? (
                    <div className="schedule-detail__loading-small">Loading...</div>
                ) : executions.length > 0 ? (
                    <div className="schedule-detail__executions">
                        <table className="schedule-detail__table">
                            <thead>
                                <tr>
                                    <th>Started</th>
                                    <th>Duration</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {executions.map(execution => (
                                    <tr key={execution.id}>
                                        <td>{formatDate(execution.startedAt)}</td>
                                        <td>{formatDuration(execution.duration)}</td>
                                        <td>
                                            <span className={`status-badge status-badge--${getStatusColor(execution.status)}`}>
                                                {execution.status}
                                            </span>
                                        </td>
                                        <td>
                                            {execution.executionPlanExecutionId && (
                                                <Link
                                                    to={`/execution-plans/executions/${execution.executionPlanExecutionId}`}
                                                    className="btn btn--small btn--secondary"
                                                >
                                                    View Details
                                                </Link>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="schedule-detail__empty">
                        <p>No executions yet</p>
                    </div>
                )}
            </div>

            <Dialog {...dialogState} onClose={closeDialog} />

            {showEditModal && (
                <ScheduleModal
                    onClose={() => setShowEditModal(false)}
                    initialData={schedule}
                    onComplete={() => {
                        setShowEditModal(false);
                        loadSchedule();
                    }}
                />
            )}
        </div>
    );
}

export default ScheduleDetail;
